import React, { createContext, useState, useContext, useEffect } from 'react';
import { ReactNode } from 'react';
import { tagService } from '../api/tagService';
import { userSettingService, NotificationSettings } from '../api/userSettings';
import { CollectionItem, CollectionList, collectionService } from '../api/collectionService';
import { FilterParams } from '../api/paginationService';
import { authService } from '../api/authService';
import subscriptionService, { Subscription } from '../api/subscriptionService';
import { purchaseService } from '../services/purchaseService';
import i18n from '../i18n';
import { trackEvent } from '../api/analyticsService';

const SUBSCRIPTION_POLL_INTERVAL_MS = 1500;
const SUBSCRIPTION_POLL_MAX_ATTEMPTS = 8;

// Mirrors the backend's UserSubscription.IsActive() — kept here (not just
// inline where `isPro` is computed) so refreshSubscriptionUntilPro can use
// the exact same check on freshly-fetched data before the next render.
export const isSubActive = (sub: Subscription | null): boolean =>
    sub !== null &&
    (sub.status === 'active' || sub.status === 'trialing') &&
    sub.plan === 'pro' &&
    (sub.current_period_end == null || new Date(sub.current_period_end) > new Date());

interface UserSettings {
    // Define the structure of user settings here
    default_languages: string;
    default_language: string;
    learning_language: string;
    daily_goal: number;
    target_level: number;
    native_language: string;
    languages: "en" | "de" | "es" | "fr" | "pl" | "ru";
    friend_id: number;
    onboarding_complete?: boolean;
    // Wizard screen reached (the API's user_settings.onboarding_step).
    onboarding_step?: number;
    display_settings?: {
        theme: string;
        interface_language: string;
    };
    integration_settings?: {
        google_translate: boolean;
        tts_voice: boolean;
        tts_voice_language: string;
        tts_voice_model: string;
        tts_voice_sample_rate: number;
        tts_voice_language_native?: string;
        tts_voice_model_native?: string;
        tts_voice_sample_rate_native?: number;
        tts_list_models?: string[];
        default_dictionary: string;
        stt_record: boolean;
    };
    notification_settings?: NotificationSettings;
    learning_settings?: {
        difficulty_level: string;
        review_interval: string;
        spaced_repetition: boolean;
        audio_pronunciation: boolean;
        enable_spelling: boolean;
        enable_type_in: boolean;
        enable_fill_blank: boolean;
        // Highest useProficiencyTier promotion offer (1 or 2) the user has already
        // responded to (confirmed or dismissed) — persisted server-side so it
        // survives across devices/browsers. Absent/0 means "never offered".
        last_offered_proficiency_tier?: number;
    };
    privacy_settings?: {
        third_party_services: boolean;
    };
}

// Switches the UI to the account's saved interface language. A new account has
// none: the server used to seed "en", which switched a user who signed up in
// Spanish to English (2026-10: all 30 new users ended up with "en"), so it now
// leaves it empty and onboarding saves the language they're actually using
// (ProfileSetup's submit).
function applyInterfaceLanguage(settings: UserSettings) {
    if (settings.display_settings?.interface_language) {
        i18n.changeLanguage(settings.display_settings.interface_language);
    }
}

interface UserTags {
    // Define the structure of user tags here
    id: number;
    tag_name: string;
    tag_description: string;
    tag_group_id: number;
    color?: string;
    icon?: string;
}

interface AuthContextType {
    user: any;
    userSettings: UserSettings | null;
    userCollection: CollectionItem[];
    userTags: UserTags[];
    isLoading: boolean;
    subscription: Subscription | null;
    isPro: boolean;
    refreshSubscription: () => Promise<void>;
    refreshSubscriptionUntilPro: () => Promise<boolean>;
    login: (userData: any, token: any, method?: string) => Promise<UserSettings | null>;
    logout: () => void;
    updateUserSettings: (settings: UserSettings) => void;
    /** Merge saved fields into the cached settings without the side effects of updateUserSettings. */
    patchUserSettings: (patch: Partial<UserSettings>) => void;
    updateUser: (userData: any) => void;
    refreshCollections: () => Promise<void>;
    refreshTags: () => Promise<void>;
    refreshUserData: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);



interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
    const [user, setUser] = useState(() => {
        try {
            const stored = localStorage.getItem('userAuth');
            return stored ? JSON.parse(stored) : null;
        } catch {
            return null;
        }
    });
    const [userSettings, setUserSettings] = useState<UserSettings | null>(null);
    const [userCollection, setCollection] = useState<CollectionItem[]>([]);
    const [userTags, setUserTags] = useState<UserTags[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [subscription, setSubscription] = useState<Subscription | null>(null);
    // Total items count for pagination




    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token || !user) {
            setIsLoading(false);
            return;
        }
        (async () => {
            try {
                const [settings, freshUser, sub] = await Promise.all([
                    fetchUserSettings(),
                    authService.getCurrentUser(),
                    subscriptionService.getSubscription().catch(() => null),
                ]);
                setSubscription(sub);
                // Sync user object so email_verified (and other fields) are always fresh
                setUser(freshUser);
                localStorage.setItem('userAuth', JSON.stringify(freshUser));
                if (freshUser?.id) purchaseService.configure(freshUser.id);

                try {
                    const tags = await fetchUserTags(settings.learning_language);
                    const updatedFilters = { ...filters, language: settings.learning_language as "en" | "de" | "es" | "fr" | "pl" | "ru" };
                    setFilters(updatedFilters);
                    const collection = await fetchUserCollection(updatedFilters);
                    setUserSettings(settings);
                    setCollection(collection.collections);
                    setUserTags(tags);
                } catch {
                    // Secondary data failed — keep the session alive, just set what we have
                    setUserSettings(settings);
                }

                applyInterfaceLanguage(settings);
            } catch (err: any) {
                // Only clear auth on definitive 401; leave session intact for transient errors
                if (err?.response?.status === 401) {
                    localStorage.removeItem('token');
                    localStorage.removeItem('userAuth');
                    setUser(null);
                }
            } finally {
                setIsLoading(false);
            }
        })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchUserSettings = async (): Promise<UserSettings> => {
        // Replace with your API call to fetch user settings
        const response = await userSettingService.getUserSettings();
        return response;
    };
    // Filter state
    const [filters, setFilters] = useState<FilterParams>({
        search: '',
        language: 'en',
        tags: []
    });
    const fetchUserTags = async (learningLanguage?: string): Promise<UserTags[]> => {
        // Replace with your API call to fetch user tags
        const response = await tagService.getUserTags(learningLanguage);

        return response;
    };
    const fetchUserCollection = async (filters: FilterParams): Promise<CollectionList> => {
        // Replace with your API call to fetch user collections
        const response = await collectionService.getAllCollection(filters);

        return response;
    };


    // `method` labels the user_login event: 'email' for the form, the provider for OAuth.
    // This is the only place user_login is tracked — callers must not track it again.
    const login = async (userData: any, token: any, method: string = 'email'): Promise<UserSettings | null> => {
        setUser(userData);
        localStorage.setItem('token', token);
        localStorage.setItem('userAuth', JSON.stringify(userData));
        trackEvent('user_login', { method }, { withDevice: true });
        try {
            if (userData && userData.id) {
                purchaseService.configure(userData.id);
                const sub = await subscriptionService.getSubscription().catch(() => null);
                setSubscription(sub);
                const settings = await fetchUserSettings();
                const tags = await fetchUserTags(settings.learning_language);
                const updatedFilters = { ...filters, language: settings.learning_language as "en" | "de" | "es" | "fr" | "pl" | "ru" };
                setFilters(updatedFilters);
                const collection = await fetchUserCollection(updatedFilters);
                setUserSettings(settings);
                setCollection(collection.collections);
                setUserTags(tags);
                applyInterfaceLanguage(settings);
                return settings;
            } else {
                console.error('User data is missing or invalid');
                setIsLoading(false);
                return null;
            }
        } catch (error) {
            console.error('Failed to fetch user collection, settings or tags:', error);
            // Roll back the optimistic user/token set above — otherwise a dead
            // token still reads as "logged in" and PrivateRoute renders /dashboard,
            // whose calls 401, redirect to /login, and retry the same dead token.
            localStorage.removeItem('token');
            localStorage.removeItem('userAuth');
            setUser(null);
            return null;
        } finally {
            setIsLoading(false);
        }
    };

    const logout = () => {
        trackEvent('user_logout');
        localStorage.removeItem('token');
        localStorage.removeItem('userAuth');
        setUser(null);
        setUserSettings(null);
        setUserTags([]);
        setCollection([]);
        setSubscription(null);
    };

    const refreshSubscription = async () => {
        try {
            const sub = await subscriptionService.getSubscription();
            setSubscription(sub);
        } catch {
            // non-fatal — leave existing state
        }
    };

    // Polls GET /subscription until it reflects an active Pro plan, or gives
    // up after SUBSCRIPTION_POLL_MAX_ATTEMPTS. Needed because RevenueCat's
    // webhook (the only path that writes Pro status to the backend, for
    // every platform) can land a second or two after the purchase already
    // resolved in the browser/app — a single refreshSubscription() call can
    // race it and read stale "free" state, which then sticks for the rest
    // of the session since nothing else re-fetches it. Used right after a
    // successful web purchase (SubscriptionTab) and on the post-checkout
    // landing page (SubscriptionSuccess).
    const refreshSubscriptionUntilPro = async (): Promise<boolean> => {
        for (let attempt = 0; attempt < SUBSCRIPTION_POLL_MAX_ATTEMPTS; attempt++) {
            try {
                const sub = await subscriptionService.getSubscription();
                const pro = isSubActive(sub);
                // Only commit an intermediate ("still free") result on the
                // last attempt. A caller (e.g. SubscriptionTab) may have
                // already set an optimistic Pro state locally right after a
                // successful purchase; committing every still-stale poll
                // result here would flicker that state back to Free each
                // time until the webhook finally lands.
                if (pro || attempt === SUBSCRIPTION_POLL_MAX_ATTEMPTS - 1) {
                    setSubscription(sub);
                }
                if (pro) return true;
            } catch {
                // ignore this attempt, try again
            }
            if (attempt < SUBSCRIPTION_POLL_MAX_ATTEMPTS - 1) {
                await new Promise(resolve => setTimeout(resolve, SUBSCRIPTION_POLL_INTERVAL_MS));
            }
        }
        return false;
    };

    const updateUserSettings = (settings: UserSettings) => {
        setUserSettings(prev => {
            if (settings.learning_language && prev?.learning_language !== settings.learning_language) {
                fetchUserTags(settings.learning_language).then(setUserTags).catch(console.error);
            }
            return settings;
        });
        setFilters(prev => ({ ...prev, language: settings.learning_language as "en" | "de" | "es" | "fr" | "pl" | "ru" }));
        applyInterfaceLanguage(settings);
    };

    const patchUserSettings = (patch: Partial<UserSettings>) => {
        setUserSettings(prev => (prev ? { ...prev, ...patch } : prev));
    };

    const updateUser = (userData: any) => {
        setUser(userData);
        if (userData) {
            localStorage.setItem('userAuth', JSON.stringify(userData));
        } else {
            localStorage.removeItem('userAuth');
        }
    };

    const refreshCollections = async () => {
        try {
            const collection = await fetchUserCollection(filters);
            setCollection(collection.collections);
        } catch (error) {
            console.error('Failed to refresh collections:', error);
        }
    };

    const refreshTags = async () => {
        try {
            const tags = await fetchUserTags(filters.language as string);
            setUserTags(tags);
        } catch (error) {
            console.error('Failed to refresh tags:', error);
        }
    };

    const refreshUserData = async () => {
        if (!user) return;

        try {
            const settings = await fetchUserSettings();
            const tags = await fetchUserTags(settings.learning_language);
            const updatedFilters = {
                ...filters,
                language: settings.learning_language as "en" | "de" | "es" | "fr" | "pl" | "ru"
            };
            const collection = await fetchUserCollection(updatedFilters);

            setUserSettings(settings);
            setCollection(collection.collections);
            setUserTags(tags);
        } catch (error) {
            console.error('Failed to refresh user data:', error);
        }
    };


    const isPro = isSubActive(subscription);

    return (
        <AuthContext.Provider
            value={{
                user,
                userSettings,
                userCollection,
                userTags,
                isLoading,
                subscription,
                isPro,
                refreshSubscription,
                refreshSubscriptionUntilPro,
                login,
                logout,
                updateUserSettings,
                patchUserSettings,
                updateUser,
                refreshCollections,
                refreshTags,
                refreshUserData
            }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    return useContext(AuthContext);
};